import React from 'react';
import { MapPin, Clock, Plane, Calendar, Globe, ArrowRight, RotateCw, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatDate, formatDuration } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета времени полета
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматируем время полета для отображения
  const formatFlightTime = (hours) => {
    const hoursInt = Math.floor(hours);
    const minutes = Math.round((hours - hoursInt) * 60);
    return `${hoursInt} ч ${minutes} мин`;
  };

  // Получаем разницу часовых поясов
  const getTimezoneDifference = () => {
    if (!results.includeTimezones || !results.departureTimezone || !results.arrivalTimezone) {
      return null;
    }

    const difference = results.arrivalTimezone.offset - results.departureTimezone.offset;
    const sign = difference >= 0 ? '+' : '';
    return `${sign}${difference} ч`;
  };

  // Форматируем даты для отображения
  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <ToolResult title="Результаты расчета времени полета">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Расстояние и время полета */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Plane className="w-4 h-4"/>
            Расстояние и время полета (туда)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.totalDistance, 0)} км
            </span>
            <span className="text-lg text-blue-600 dark:text-blue-400">
              / {formatFlightTime(results.totalFlightTimeOutbound)}
            </span>
          </div>
          {results.includeLayover && results.layoverCity && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Включая пересадку в {results.layoverCity} ({formatFlightTime(results.totalLayoverTimeOutbound)})
            </p>
          )}
        </div>

        {/* Общее время в пути */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Общее время в пути
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatFlightTime(results.totalJourneyTime)}
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {results.isRoundTrip ? 'Включая время обратного полета' : 'Только в одну сторону'}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Маршрут" 
          value={`${results.departureCity} → ${results.arrivalCity}${results.layoverCity ? ` через ${results.layoverCity}` : ''}`}
          color="blue" 
          icon={<MapPin className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Вылет" 
          value={formatDateTime(results.departureDate)}
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Прибытие" 
          value={formatDateTime(results.arrivalDate)}
          icon={<ArrowRight className="w-4 h-4"/>} 
        />
        
        {results.isRoundTrip && results.returnDepartureDate && (
          <>
            <ResultItem 
              label="Обратный вылет" 
              value={formatDateTime(results.returnDepartureDate)}
              icon={<RotateCw className="w-4 h-4"/>} 
            />
            
            <ResultItem 
              label="Прибытие обратно" 
              value={formatDateTime(results.returnArrivalDate)}
              icon={<ArrowRight className="w-4 h-4"/>} 
            />
          </>
        )}

        <ResultItem 
          label="Самолет" 
          value={`${results.aircraftName} (средняя скорость ${results.aircraftSpeed} км/ч)`}
          icon={<Plane className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Прямое расстояние" 
          value={`${formatNumber(results.directDistance, 0)} км`}
          color="blue" 
        />
        
        <ResultItem 
          label="Фактическое расстояние" 
          value={`${formatNumber(results.totalDistance, 0)} км`}
          color="blue" 
        />

        {results.includeDelays && (
          <ResultItem 
            label="Учтены задержки" 
            value={`+${Math.round(results.delayMultiplier * 100)}% ко времени`}
            color="amber" 
          />
        )}
        
        {getTimezoneDifference() && (
          <ResultItem 
            label="Разница часовых поясов" 
            value={getTimezoneDifference()}
            color="purple" 
            icon={<Globe className="w-4 h-4"/>} 
          />
        )}

        {/* Информация о часовых поясах */}
        {results.includeTimezones && results.departureTimezone && results.arrivalTimezone && (
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 mt-2 border border-purple-200 dark:border-purple-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Globe className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Информация о часовых поясах</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {results.departureCity}: {results.departureTimezone.name} (UTC{results.departureTimezone.offset >= 0 ? '+' : ''}{results.departureTimezone.offset})
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {results.arrivalCity}: {results.arrivalTimezone.name} (UTC{results.arrivalTimezone.offset >= 0 ? '+' : ''}{results.arrivalTimezone.offset})
                </p>
                {results.layoverCity && results.layoverTimezone && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {results.layoverCity}: {results.layoverTimezone.name} (UTC{results.layoverTimezone.offset >= 0 ? '+' : ''}{results.layoverTimezone.offset})
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Примечание о расчете */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Примечание о расчете</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Время полета рассчитано на основе средней крейсерской скорости выбранного типа самолета. 
                Фактическое время может отличаться в зависимости от погодных условий, маршрута и модели воздушного судна.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;